import { useLayoutEffect, useRef, useState } from "react";

type TruncationAxis = "horizontal" | "vertical" | "both";

type UseIsTruncatedOptions = {
    axis?: TruncationAxis;
    enabled?: boolean;
};

const getKeyHash = (key?: unknown | unknown[]) => {
    if (Array.isArray(key)) {
        return key.map((item) => String(item ?? "")).join("|");
    }
    return String(key ?? "");
};

const isElementTruncated = (el: HTMLElement, axis: TruncationAxis) => {
    const isHorizontal = el.scrollWidth > el.clientWidth;
    const isVertical = el.scrollHeight > el.clientHeight;

    if (axis === "vertical") return isVertical;
    if (axis === "both") return isHorizontal || isVertical;
    return isHorizontal;
};

const useIsTruncated = <T extends HTMLElement = HTMLElement>(
    key?: unknown | unknown[],
    options?: UseIsTruncatedOptions
) => {
    const ref = useRef<T | null>(null);
    
    const [isTruncated, setIsTruncated] = useState(false);
    
    const axis = options?.axis ?? "horizontal";
    const enabled = options?.enabled ?? true;
    const keyHash = getKeyHash(key);

    useLayoutEffect(() => {
        const updateTruncation = () => {
            const el = ref.current;
            if (!el || !enabled) {
                setIsTruncated(false);
                return;
            }
            setIsTruncated(isElementTruncated(el, axis));
        };

        updateTruncation();
        const el = ref.current;
        if (!el || !enabled || typeof ResizeObserver === "undefined") return;

        const observer = new ResizeObserver(updateTruncation);
        observer.observe(el);

        return () => observer.disconnect();
    }, [keyHash, axis, enabled]);

    return { ref, isTruncated };
};

export default useIsTruncated;
